import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '@/shared/utils';
import {
  SEARCH_SCROLL_FADE_BOTH_CLASSNAME,
  SEARCH_SCROLL_FADE_BOTTOM_CLASSNAME,
  SEARCH_SCROLL_FADE_TOP_CLASSNAME,
  SEARCH_SCROLL_LIST_CLASSNAME,
} from '../config/styles';

type ScrollState = {
  top: boolean;
  bottom: boolean;
  scrollbarWidth: number;
};

const getFadeClassName = ({ top, bottom }: ScrollState) => {
  if (top && bottom) {
    return SEARCH_SCROLL_FADE_BOTH_CLASSNAME;
  }

  if (top) {
    return SEARCH_SCROLL_FADE_TOP_CLASSNAME;
  }

  return bottom ? SEARCH_SCROLL_FADE_BOTTOM_CLASSNAME : undefined;
};

type Props = {
  className?: string;
  children: ReactNode;
};

const SearchScrollList = ({ className, children }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<ScrollState>({
    top: false,
    bottom: false,
    scrollbarWidth: 0,
  });

  const updateScrollState = useCallback(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const top = element.scrollTop > 0;
    const bottom =
      element.scrollTop + element.clientHeight < element.scrollHeight - 1;

    const scrollbarWidth = element.offsetWidth - element.clientWidth;

    setState(prev =>
      prev.top === top &&
      prev.bottom === bottom &&
      prev.scrollbarWidth === scrollbarWidth
        ? prev
        : { top, bottom, scrollbarWidth }
    );
  }, []);

  useLayoutEffect(updateScrollState);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(element);

    return () => observer.disconnect();
  }, [updateScrollState]);

  return (
    <div
      ref={ref}
      onScroll={updateScrollState}
      style={{ paddingRight: `calc(2.4rem - ${state.scrollbarWidth}px)` }}
      className={cn(
        SEARCH_SCROLL_LIST_CLASSNAME,
        getFadeClassName(state),
        className
      )}>
      {children}
    </div>
  );
};

export { SearchScrollList };
